// sim/hive.ts — the PHYSICAL HIVE: the colony's nest as real on-map structure.
//
// Phase 4 gave each colony a list of lightweight "hive nodes" (just coords the
// AI founds when Expansion wins). This system turns those nodes into REAL
// structures and is a strict READ-ONLY CONSUMER of the autonomy layer — it never
// mutates colonyAI / colony / castes; it only reads their public accessors.
//
// A physical hive is two pieces of pure integer state:
//
//   1. CHAMBERS (rooms) — an integer-id row in per-colony SoA columns
//      (index [cid*maxPer + k]): a typed room (Nursery/Storage/Military/Mutation)
//      at an integer centre, with a structural LEVEL and a claimed-tile count.
//      A chamber is never a heap object — same data-oriented mandate as organisms
//      / colonies / genes (DESIGN.md §2).
//
//   2. TERRITORY — a per-tile owner map the size of the grid (territoryOwner),
//      OWNED BY THIS SYSTEM (no new WorldGrid columns). A tile is claimed by at
//      most one colony; once claimed it is never STOLEN. The one release path is
//      EXTINCTION: when a colony dies (colony.isExtinct) its whole hold — every
//      claimed tile + its chambers — reverts to unowned, ONCE (latched), so the
//      map does not stay frozen around a wiped-out colony.
//
// EXPANSION IS AUTONOMOUS. There is NO player placement. Every colony gets a
// Nursery CORE at its nest on tick 0 (so it is a physical hive from the start);
// thereafter chambers are REALISED one-for-one from the AI's founded hive nodes,
// each tile-claimed and TUNNEL-connected (an L-path the chamber excavates) to the
// nearest existing same-colony chamber. EVOLUTION drives growth: the more genes a
// colony holds, the higher its chambers' target level, and an upgrade regrows the
// room to a larger radius, claiming new ground. Mutation chambers periodically
// auto-splice the colony's genes; Nurseries pay a periodic food dividend.
//
// DETERMINISM: pure integer SoA (exact FNV hashing), fixed colony/chamber/tile
// iteration order, RNG only from ctx (unused today — all decisions are integer).
// Allocation-free in update(): the claim/tunnel loops iterate FIXED bounds with
// no temp arrays or closures.

import { CHAMBER, KIND, NEED, NIL } from '../types.ts';
import type {
  System,
  Tick,
  ColonyId,
  ChamberType,
  HiveConfig,
} from '../types.ts';
import type { WorldGrid } from '../engine/WorldGrid.ts';
import type { EntitySystem } from '../engine/EntitySystem.ts';
import type { Rng } from '../rng.ts';
import type { ColonySystem } from './colony.ts';
import type { ColonyAISystem } from './colonyAI.ts';
import type { GeneInventory, GeneId } from './genes.ts';

/** Sentinel for a per-colony tick gate that has "never fired" — far below any real tick. */
const NEVER = -1 << 30;

/**
 * The small bridge colonyAI consumes to post AND resolve hive-tending tasks
 * (Nurse / Haul / Repair) — it never reaches into the hive's internals. Late-bound
 * into the AI via `attachHive` to keep the one-way dependency (hive reads AI, not
 * the reverse) from becoming a construction cycle. Absent ⇒ the AI never posts the
 * three hive tasks (the arrival callbacks no-op), so a world without a hive is
 * byte-identical. HiveSystem implements this directly (the methods live on the
 * class). The READ half drives the AI's enqueue gates; the EFFECT half is fired by
 * the task pursuit on a tending organism's arrival.
 */
export interface HiveRead {
  // -- read half (enqueue gates) --
  /** True if `cid` holds a chamber of CHAMBER.* `type`. */
  hasChamber(cid: ColonyId, type: number): boolean;
  /** Write the first chamber of `type`'s centre to out[0]/out[1]; false if none. */
  chamberCentreOfType(cid: ColonyId, type: number, out: Int32Array): boolean;
  /** Slot of the most-degraded non-core chamber at/below repairUrgency, or -1. */
  mostDegradedChamber(cid: ColonyId): number;
  /** Write chamber slot k's centre to out[0]/out[1]; false if k is not live. */
  chamberCentreAt(cid: ColonyId, k: number, out: Int32Array): boolean;
  // -- effect half (task arrival) --
  /** Nurse arrival: accelerate the colony's brood. */
  nurseTend(cid: ColonyId): void;
  /** Haul arrival: release Storage reserve food into the colony. */
  haulFromStorage(cid: ColonyId): void;
  /** Repair arrival: restore the most-degraded chamber's integrity. */
  repairChamber(cid: ColonyId): void;
}

export class HiveSystem implements System {
  readonly name = 'hive';

  private readonly colonies: ColonySystem;
  private readonly ai: ColonyAISystem;
  private readonly entities: EntitySystem;
  private readonly grid: WorldGrid;
  private readonly rng: Rng;
  private readonly genes: GeneInventory | undefined;
  private readonly config: HiveConfig;

  readonly maxColonies: number;
  private readonly maxPer: number;

  // --- Chamber SoA (per-colony, index [cid*maxPer + k]). ---
  private readonly chamberType: Uint8Array;
  private readonly chamberX: Int32Array;
  private readonly chamberY: Int32Array;
  private readonly chamberLevel: Int32Array;
  private readonly chamberTiles: Int32Array;
  /**
   * Chamber REPAIR METER (structural integrity, 0..repairMax). The minimal honest
   * damage model: a fresh chamber sits at repairMax; non-core chambers WEAR down by
   * chamberDecay each effect cadence and a Repair task tops them back up. The core
   * Nursery never wears (a colony is never crippled by maintenance). Folded into the
   * hash — its movement is load-bearing for determinism.
   */
  private readonly chamberRepair: Int32Array;
  private readonly chamberCount: Int32Array;

  // --- Territory: per-tile colony owner (NIL = unclaimed) + per-colony count. ---
  private readonly territoryOwner: Int32Array;
  private readonly territoryCount: Int32Array;

  // --- Bookkeeping. ---
  /** How many of the AI's founded hive nodes have become chambers, per colony. */
  private readonly realizedNodes: Int32Array;
  /** 1 once the colony's Nursery core chamber has been built. */
  private readonly coreBuilt: Uint8Array;
  /** Per-colony tick gate for Mutation auto-splice. */
  private readonly lastMutation: Int32Array;
  /**
   * 1 once a colony's territory has been reclaimed after it went extinct. A latch,
   * so the (full-grid) reclaim scan runs AT MOST ONCE per colony over the whole run
   * rather than every tick the colony stays dead — the per-tick hive maintenance
   * just reads this flag.
   */
  private readonly reclaimed: Uint8Array;

  // --- Public counters (deterministic; folded into the hash, read by tests). ---
  chambersBuiltTotal = 0;
  tilesClaimedTotal = 0;
  tunnelsCarvedTotal = 0;
  upgradesTotal = 0;
  mutationSplicesTotal = 0;
  /** Total food yielded into colonies by Storage chambers. */
  storageYieldTotal = 0;
  /** Total rival-organism strikes dealt by Military chambers. */
  militaryHitsTotal = 0;
  /** Total spawn-cooldown ticks Nurse tasks have shaved off colony brood. */
  nurseBoostTotal = 0;
  /** Total food Haul tasks have moved from Storage chambers into colony stores. */
  haulMovedTotal = 0;
  /** Total integrity points Repair tasks have restored to degraded chambers. */
  repairedTotal = 0;

  constructor(
    colonies: ColonySystem,
    ai: ColonyAISystem,
    entities: EntitySystem,
    grid: WorldGrid,
    rng: Rng,
    genes: GeneInventory | undefined,
    config: HiveConfig,
  ) {
    this.colonies = colonies;
    this.ai = ai;
    this.entities = entities;
    this.grid = grid;
    this.rng = rng;
    this.genes = genes;
    this.config = config;
    this.maxColonies = colonies.capacity;
    this.maxPer = config.maxChambersPerColony;

    const chamberCap = this.maxColonies * this.maxPer;
    this.chamberType = new Uint8Array(chamberCap);
    this.chamberX = new Int32Array(chamberCap);
    this.chamberY = new Int32Array(chamberCap);
    this.chamberLevel = new Int32Array(chamberCap);
    this.chamberTiles = new Int32Array(chamberCap);
    this.chamberRepair = new Int32Array(chamberCap);
    this.chamberCount = new Int32Array(this.maxColonies);

    this.territoryOwner = new Int32Array(grid.size).fill(NIL);
    this.territoryCount = new Int32Array(this.maxColonies);

    this.realizedNodes = new Int32Array(this.maxColonies);
    this.coreBuilt = new Uint8Array(this.maxColonies);
    this.lastMutation = new Int32Array(this.maxColonies).fill(NEVER);
    this.reclaimed = new Uint8Array(this.maxColonies);
  }

  // --- Per-tick: build core → realise nodes → effects → upgrade --------------

  update(tick: Tick): void {
    const count = this.colonies.count;
    const effOn = tick % this.config.effectInterval === 0;
    for (let cid = 0; cid < count; cid++) {
      // (0) Extinction reclaim: a colony that has lost its last body and cannot
      //     rebuild (colony.isExtinct) gives its ground back to the world — its
      //     claimed tiles revert to unowned and its physical hive is razed. Latched
      //     by `reclaimed` so the full-grid release runs ONCE, not every dead tick.
      //     A dead colony does nothing else this tick (it cannot build or expand).
      if (this.colonies.isExtinct(cid)) {
        if (this.reclaimed[cid] === 0) this.reclaimTerritory(cid);
        continue;
      }

      // (1) Core chamber: every colony becomes a physical hive from tick 0.
      if (this.coreBuilt[cid] === 0) {
        const hx = this.colonies.homeXAt(cid);
        const hy = this.colonies.homeYAt(cid);
        this.buildChamber(cid, hx, hy, CHAMBER.Nursery);
        this.coreBuilt[cid] = 1;
      }

      // (2) Realise the AI's founded hive nodes into typed chambers, in node
      //     order, until we run out of nodes or hit the per-colony chamber cap.
      const nodes = this.ai.hiveCountAt(cid);
      while (
        (this.realizedNodes[cid] as number) < nodes &&
        (this.chamberCount[cid] as number) < this.maxPer
      ) {
        const k = this.realizedNodes[cid] as number;
        const nx = this.ai.hiveXAt(cid, k);
        const ny = this.ai.hiveYAt(cid, k);
        // Type is the colony's economic argmax — UNLESS a hostile is physically on
        // top of the build site RIGHT NOW. The AI's Defense need is refreshed only
        // on its decision cadence, so a threat that arrives between ticks would not
        // raise a war-room until the next decision; a cheap LIVE scan of the node
        // (below) closes that gap so a chamber raised into a hot spot fortifies
        // immediately. We only override to Military if the colony lacks one already.
        let type = this.chooseChamberType(cid);
        if (
          type !== CHAMBER.Military &&
          !this.hasChamberType(cid, CHAMBER.Military) &&
          this.hasLiveThreat(cid, nx, ny)
        ) {
          type = CHAMBER.Military;
        }
        this.buildChamber(cid, nx, ny, type);
        this.realizedNodes[cid] = k + 1;
      }

      // (2.5) Siege response: a besieged colony fortifies its nest with a Military
      //       war-room (built once), independent of economic expansion — the AI
      //       only EXPANDS when not under threat, so a war-room never arrives via
      //       a realised node. The garrison strikes besiegers in applyEffects.
      if (
        (this.chamberCount[cid] as number) < this.maxPer &&
        this.ai.needAt(cid, NEED.Defense) >= this.config.militaryUrgency &&
        !this.hasChamberType(cid, CHAMBER.Military)
      ) {
        this.buildChamber(cid, this.colonies.homeXAt(cid), this.colonies.homeYAt(cid), CHAMBER.Military);
      }

      // (2.6) Stockpile response: a food-stressed colony raises a Storage room at
      //       its nest (built once) so its reserve yield eases the famine — like
      //       the siege response, this is independent of expansion (a hungry colony
      //       forages rather than expands, so a Storage room would not otherwise
      //       arrive via a realised node, where food drivers favour the well-fed).
      if (
        (this.chamberCount[cid] as number) < this.maxPer &&
        this.ai.needAt(cid, NEED.Food) >= this.config.storageUrgency &&
        !this.hasChamberType(cid, CHAMBER.Storage)
      ) {
        this.buildChamber(cid, this.colonies.homeXAt(cid), this.colonies.homeYAt(cid), CHAMBER.Storage);
      }

      // (3) Periodic chamber effects (Nursery dividend, Mutation auto-splice).
      if (effOn) this.applyEffects(cid, tick);

      // (4) Evolution upgrades: more genes ⇒ higher target structural level.
      this.applyUpgrades(cid);
    }
  }

  // --- Effects ---------------------------------------------------------------

  /** Run each of the colony's chambers' periodic effect (on the effect cadence). */
  private applyEffects(cid: ColonyId, tick: Tick): void {
    const cfg = this.config;
    const n = this.chamberCount[cid] as number;
    const base = cid * this.maxPer;
    let storageLevel = 0;
    for (let k = 0; k < n; k++) {
      const idx = base + k;
      const type = this.chamberType[idx] as number;
      const level = this.chamberLevel[idx] as number;
      // WEAR: every non-core chamber (k > 0) loses a little integrity each cadence.
      // The core (k === 0) never wears, so a colony is never crippled by upkeep; an
      // expanded colony, though, must repair its outlying rooms (drives the Repair
      // task). Clamped at 0. Decay 0 ⇒ chambers never degrade (byte-identical).
      if (k > 0 && cfg.chamberDecay > 0) {
        const r = (this.chamberRepair[idx] as number) - cfg.chamberDecay;
        this.chamberRepair[idx] = r > 0 ? r : 0;
      }
      if (type === CHAMBER.Nursery) {
        // A Nursery converts colony space into growth: a food dividend × level.
        this.colonies.depositFood(cid, cfg.nurseryDividend * level);
      } else if (type === CHAMBER.Mutation) {
        // A Mutation room auto-splices the colony's two newest genes on its own
        // gate, so evolution happens physically inside the hive (autonomous).
        if (
          tick - (this.lastMutation[cid] as number) >= cfg.mutationInterval &&
          this.genes !== undefined &&
          this.genes.countOf(cid) >= 2
        ) {
          if (this.spliceNewest(cid)) {
            this.mutationSplicesTotal++;
            this.lastMutation[cid] = tick;
          }
        }
      } else if (type === CHAMBER.Storage) {
        // A Storage chamber cultivates reserves; its level feeds the steady food
        // yield released into the colony after the loop (rooms pool their level).
        storageLevel += level;
      } else if (type === CHAMBER.Military) {
        // A Military chamber is a fortified outpost: it strikes rival organisms
        // within range, so the hive defends its own ground (damage × level).
        this.garrisonStrike(
          cid,
          this.chamberX[idx] as number,
          this.chamberY[idx] as number,
          cfg.militaryDamage * level,
        );
      }
    }
    // Storage yield: the pooled reserve feeds a steady food dividend into the colony.
    if (storageLevel > 0) {
      const yielded = cfg.storageYield * storageLevel;
      this.colonies.depositFood(cid, yielded);
      this.storageYieldTotal += yielded;
    }
  }

  /**
   * A Military chamber's defensive strike: subtract `damage` hp from every live
   * RIVAL-colony organism within `militaryRadius` (Chebyshev) of (cx,cy). Never
   * touches own-colony members (no friendly fire), wildlife, or food. The owning
   * combat loop reaps anything left at hp<=0 on the next tick. Allocation-free,
   * deterministic (fixed tile + bucket iteration order).
   */
  private garrisonStrike(cid: ColonyId, cx: number, cy: number, damage: number): void {
    if (damage <= 0) return;
    const grid = this.grid;
    const e = this.entities;
    const r = this.config.militaryRadius;
    const minX = cx - r < 0 ? 0 : cx - r;
    const minY = cy - r < 0 ? 0 : cy - r;
    const maxX = cx + r >= grid.width ? grid.width - 1 : cx + r;
    const maxY = cy + r >= grid.height ? grid.height - 1 : cy + r;
    for (let ty = minY; ty <= maxY; ty++) {
      for (let tx = minX; tx <= maxX; tx++) {
        const ti = grid.index(tx, ty);
        for (let cur = grid.headAt(ti); cur !== NIL; cur = grid.next(cur)) {
          const id = cur as number;
          if ((e.kind[id] as number) !== KIND.Organism) continue;
          if ((e.colony[id] as number) === cid) continue; // no friendly fire
          if ((e.hp[id] as number) <= 0) continue;
          e.hp[id] = (e.hp[id] as number) - damage;
          this.militaryHitsTotal++;
        }
      }
    }
  }

  /** Splice the colony's two most-recent genes (firstOf / nextOf). */
  private spliceNewest(cid: ColonyId): boolean {
    const genes = this.genes;
    if (genes === undefined) return false;
    const a: GeneId = genes.firstOf(cid);
    if (a === NIL) return false;
    const b: GeneId = genes.nextOf(a);
    if (b === NIL) return false;
    return genes.spliceGenes(a, b) !== NIL;
  }

  // --- Evolution-driven upgrades ---------------------------------------------

  /** Raise each chamber toward the colony's gene-derived target level. */
  private applyUpgrades(cid: ColonyId): void {
    const cfg = this.config;
    const heldGenes = this.genes !== undefined ? this.genes.countOf(cid) : 0;
    let target = 1 + Math.floor(heldGenes / cfg.upgradeGeneThreshold);
    if (target > cfg.maxChamberLevel) target = cfg.maxChamberLevel;
    const n = this.chamberCount[cid] as number;
    const base = cid * this.maxPer;
    for (let k = 0; k < n; k++) {
      const idx = base + k;
      if ((this.chamberLevel[idx] as number) < target) {
        this.upgradeChamber(cid, k);
      }
    }
  }

  // --- Chamber-type choice (deterministic argmax over the four drivers) ------

  /**
   * Choose a chamber type for a realised EXPANSION node by the colony's most
   * pressing economic driver — argmax with a fixed tie-break (Nursery < Storage <
   * Mutation): populous → Nursery, food-rich → Storage, gene-rich → Mutation.
   * (Military is NOT an economic choice — it is raised by the siege response in
   * update(), since the AI only expands when it is not itself under threat.)
   * Deterministic.
   */
  chooseChamberType(cid: ColonyId): ChamberType {
    const nursery = this.colonies.populationAt(cid);
    const storage = this.colonies.foodAt(cid);
    const mutation = this.genes !== undefined ? this.genes.countOf(cid) : 0;

    // Argmax with strictly-greater comparisons ⇒ lowest index wins ties.
    let best = nursery;
    let type: ChamberType = CHAMBER.Nursery;
    if (storage > best) {
      best = storage;
      type = CHAMBER.Storage;
    }
    if (mutation > best) {
      best = mutation;
      type = CHAMBER.Mutation;
    }
    return type;
  }

  /**
   * Cheap LIVE threat probe at a chamber-build site: true iff a hostile is present
   * within `liveThreatRadius` (Chebyshev) of (cx,cy) on THIS tick — a rival-colony
   * organism, or any wildlife/apex (the same "threat" colonyAI scans for, plus the
   * non-colony predators). Short-circuits on the first hostile, so the worst case is
   * one bounded grid-bucket walk over a (2r+1)² block — O(small), allocation-free,
   * deterministic (fixed tile + bucket iteration order). This is intentionally NOT
   * colonyAI's full cadence scan; it is the gap-filler that lets a chamber raised
   * into a momentary threat fortify before the AI's stale Defense need catches up.
   */
  private hasLiveThreat(cid: ColonyId, cx: number, cy: number): boolean {
    const grid = this.grid;
    const e = this.entities;
    const r = this.config.liveThreatRadius;
    if (r < 0) return false;
    const minX = cx - r < 0 ? 0 : cx - r;
    const minY = cy - r < 0 ? 0 : cy - r;
    const maxX = cx + r >= grid.width ? grid.width - 1 : cx + r;
    const maxY = cy + r >= grid.height ? grid.height - 1 : cy + r;
    for (let ty = minY; ty <= maxY; ty++) {
      for (let tx = minX; tx <= maxX; tx++) {
        const ti = grid.index(tx, ty);
        for (let cur = grid.headAt(ti); cur !== NIL; cur = grid.next(cur)) {
          const id = cur as number;
          if ((e.hp[id] as number) <= 0) continue; // already dead this tick
          const kind = e.kind[id] as number;
          if (kind === KIND.Organism) {
            // A rival-colony member is a threat; own members never are.
            if ((e.colony[id] as number) !== cid) return true;
          } else if (kind === KIND.Wildlife || kind === KIND.Apex) {
            // Non-colony predators/bosses are always hostile to the hive.
            return true;
          }
        }
      }
    }
    return false;
  }

  /** True if `cid` already holds a chamber of `type`. */
  private hasChamberType(cid: ColonyId, type: number): boolean {
    const n = this.chamberCount[cid] as number;
    const base = cid * this.maxPer;
    for (let k = 0; k < n; k++) if ((this.chamberType[base + k] as number) === type) return true;
    return false;
  }

  // --- Building (chamber record + room generation + tunnel) ------------------

  /**
   * Record a level-1 chamber of `type` centred at (cx,cy), grow its room (claim a
   * (2*roomRadius+1)² block, excavating walls if tunnelCarves), and — for any
   * chamber after the core — carve a tunnel to the nearest existing same-colony
   * chamber. Returns the chamber slot k, or NIL if the colony is at its cap.
   */
  private buildChamber(cid: ColonyId, cx: number, cy: number, type: ChamberType): number {
    const k = this.chamberCount[cid] as number;
    if (k >= this.maxPer) return NIL;
    const idx = cid * this.maxPer + k;
    this.chamberType[idx] = type;
    this.chamberX[idx] = cx;
    this.chamberY[idx] = cy;
    this.chamberLevel[idx] = 1;
    this.chamberTiles[idx] = 0;
    this.chamberRepair[idx] = this.config.repairMax; // a fresh chamber is at full integrity
    this.chamberCount[cid] = k + 1;
    this.chambersBuiltTotal++;

    this.generateRoom(cid, k, this.config.roomRadius);

    // The first chamber (core) connects to nothing; every later chamber tunnels
    // back to the nearest sibling so the hive stays one connected structure.
    if (k > 0) this.carveTunnel(cid, k);
    return k;
  }

  /**
   * Claim the (2*radius+1)² block around chamber k's centre for `cid`. A tile
   * already owned by ANOTHER colony is left untouched (never stolen); a wall is
   * excavated when tunnelCarves. Counts only tiles newly claimed by this colony,
   * so re-growing at a larger radius (upgrades) is idempotent on shared tiles.
   */
  private generateRoom(cid: ColonyId, k: number, radius: number): void {
    const grid = this.grid;
    const idx = cid * this.maxPer + k;
    const cx = this.chamberX[idx] as number;
    const cy = this.chamberY[idx] as number;
    const minX = cx - radius;
    const maxX = cx + radius;
    const minY = cy - radius;
    const maxY = cy + radius;
    for (let ty = minY; ty <= maxY; ty++) {
      if (ty < 0 || ty >= grid.height) continue;
      for (let tx = minX; tx <= maxX; tx++) {
        if (tx < 0 || tx >= grid.width) continue;
        if (this.claimTile(cid, tx, ty)) {
          this.chamberTiles[idx] = (this.chamberTiles[idx] as number) + 1;
        }
      }
    }
  }

  /**
   * Carve an L-shaped tunnel (horizontal then vertical) from chamber k's centre
   * to the NEAREST existing same-colony chamber centre (Chebyshev). Claims each
   * path tile for `cid` and, when tunnelCarves, excavates walls so the corridor
   * is passable. Fixed-bound, allocation-free.
   */
  private carveTunnel(cid: ColonyId, k: number): void {
    const base = cid * this.maxPer;
    const idx = base + k;
    const cx = this.chamberX[idx] as number;
    const cy = this.chamberY[idx] as number;

    // Nearest other same-colony chamber centre (Chebyshev), fixed tie-break by k.
    let tx = cx;
    let ty = cy;
    let bestD = -1;
    for (let j = 0; j < k; j++) {
      const jx = this.chamberX[base + j] as number;
      const jy = this.chamberY[base + j] as number;
      const dx = jx - cx < 0 ? cx - jx : jx - cx;
      const dy = jy - cy < 0 ? cy - jy : jy - cy;
      const d = dx > dy ? dx : dy;
      if (bestD < 0 || d < bestD) {
        bestD = d;
        tx = jx;
        ty = jy;
      }
    }
    if (bestD < 0) return; // no sibling (shouldn't happen for k>0)

    // Horizontal leg along the chamber row, then vertical leg to the target.
    const stepX = tx > cx ? 1 : -1;
    for (let x = cx; x !== tx; x += stepX) this.tunnelTile(cid, x, cy);
    const stepY = ty > cy ? 1 : -1;
    for (let y = cy; y !== ty; y += stepY) this.tunnelTile(cid, tx, y);
    this.tunnelTile(cid, tx, ty); // close the corner on the target centre

    this.tunnelsCarvedTotal++;
  }

  /** Claim + (when tunnelCarves) excavate a single tunnel tile. */
  private tunnelTile(cid: ColonyId, x: number, y: number): void {
    if (!this.grid.inBounds(x, y)) return;
    this.claimTile(cid, x, y);
    if (this.config.tunnelCarves && !this.grid.isPassable(x, y)) {
      this.grid.setPassable(x, y, true);
    }
  }

  /**
   * Claim tile (x,y) for `cid` if it is unclaimed, and excavate it if it is a
   * wall and tunnelCarves is on (a chamber digs out its own footprint). Returns
   * true iff the tile was newly claimed BY THIS COLONY. A tile owned by another
   * colony is left alone (territory is never stolen). Assumes (x,y) in bounds.
   */
  private claimTile(cid: ColonyId, x: number, y: number): boolean {
    const ti = this.grid.index(x, y);
    const owner = this.territoryOwner[ti] as number;
    if (owner === cid) return false; // already ours
    if (owner !== NIL) return false; // owned by a rival — never steal
    this.territoryOwner[ti] = cid;
    this.territoryCount[cid] = (this.territoryCount[cid] as number) + 1;
    this.tilesClaimedTotal++;
    // The chamber/room excavates its own footprint when carving is enabled.
    if (this.config.tunnelCarves && !this.grid.isPassable(x, y)) {
      this.grid.setPassable(x, y, true);
    }
    return true;
  }

  /** Regrow chamber k's room at its NEW (just-raised) level radius and bump it. */
  private upgradeChamber(cid: ColonyId, k: number): void {
    const idx = cid * this.maxPer + k;
    const newLevel = (this.chamberLevel[idx] as number) + 1;
    this.chamberLevel[idx] = newLevel;
    // Room half-extent grows by one tile per level beyond the first.
    const radius = this.config.roomRadius + (newLevel - 1);
    this.generateRoom(cid, k, radius); // claims only new tiles — never un-claims
    this.upgradesTotal++;
  }

  // --- Extinction reclaim ----------------------------------------------------

  /**
   * Reclaim ALL ground of an extinct colony: release every tile it owns back to
   * unowned (NIL), zero its `territoryCount`, and raze its chambers (drop the
   * count to 0). The hash counts territory + chambers per colony, so both drop to
   * 0 for the dead colony — the intended census change of "the wiped-out colony's
   * hold on the map is gone." A LIVING colony's tiles are untouched (only tiles
   * owned by `cid` are released). One full-grid pass; called at most once per
   * colony (latched by `reclaimed`). Allocation-free, deterministic (fixed tile
   * iteration order). Note: tunnel tiles are left passable — the dirt stays dug;
   * only OWNERSHIP is released, mirroring how a claimed tile never un-carves.
   */
  private reclaimTerritory(cid: ColonyId): void {
    const owner = this.territoryOwner;
    const n = owner.length;
    for (let ti = 0; ti < n; ti++) {
      if ((owner[ti] as number) === cid) owner[ti] = NIL;
    }
    this.territoryCount[cid] = 0;
    // Raze the physical hive: the chamber rows are now stale (their tiles are
    // unowned), so drop the count to 0. Leaving them would report phantom rooms
    // for a colony that no longer exists. Slots are reused only if the colony id
    // were ever re-founded, which the colony layer does not do.
    this.chamberCount[cid] = 0;
    this.reclaimed[cid] = 1;
  }

  // --- Task EFFECTS (invoked on a tasked organism's ARRIVAL via the AI callbacks) -
  //
  // colonyAI binds these to the AutonomyCtx onNurse/onHaul/onRepair callbacks, so a
  // Worker/Specialist that reaches a chamber performs the hive-side effect here. The
  // hive stays the single owner of chamber state; taskQueue never imports it.

  /**
   * NURSE arrival at the colony's Nursery: hurry the next brood along by shaving
   * `nurseSpawnBoost` ticks off the colony's spawn cooldown (brood acceleration).
   * Only fires if the colony actually has a Nursery. Deterministic, allocation-free.
   */
  nurseTend(cid: ColonyId): void {
    if (!this.hasChamberType(cid, CHAMBER.Nursery)) return;
    const shaved = this.colonies.hurryBrood(cid, this.config.nurseSpawnBoost);
    this.nurseBoostTotal += shaved;
  }

  /**
   * HAUL arrival at the nest: release `haulAmount` of reserve food from a Storage
   * chamber into the colony store (food distribution from store to needy nest).
   * Only fires if the colony has a Storage chamber. The reserve is abstract (the
   * Storage chamber represents stockpiled food), so this is a deposit into the
   * colony — the same store Storage's periodic yield feeds. Deterministic.
   */
  haulFromStorage(cid: ColonyId): void {
    if (!this.hasChamberType(cid, CHAMBER.Storage)) return;
    this.colonies.depositFood(cid, this.config.haulAmount);
    this.haulMovedTotal += this.config.haulAmount;
  }

  /**
   * REPAIR arrival at a chamber: restore `repairAmount` integrity to the colony's
   * MOST-degraded non-core chamber (the same one the AI targeted), capped at
   * repairMax. Deterministic tie-break (lowest slot index — see mostDegradedChamber).
   */
  repairChamber(cid: ColonyId): void {
    const k = this.mostDegradedChamber(cid);
    if (k < 0) return;
    const idx = cid * this.maxPer + k;
    const before = this.chamberRepair[idx] as number;
    let r = before + this.config.repairAmount;
    if (r > this.config.repairMax) r = this.config.repairMax;
    this.chamberRepair[idx] = r;
    this.repairedTotal += r - before;
  }

  /**
   * Slot k of the colony's MOST-degraded non-core chamber whose integrity is at or
   * below repairUrgency, or -1 if none qualifies. Fixed tie-break: the lowest slot
   * index among those at the minimum integrity (deterministic). The core (k === 0)
   * never wears, so it is skipped. O(chambers) on the decision cadence.
   */
  mostDegradedChamber(cid: ColonyId): number {
    const n = this.chamberCount[cid] as number;
    const base = cid * this.maxPer;
    let best = -1;
    let bestRepair = this.config.repairUrgency;
    for (let k = 1; k < n; k++) {
      const r = this.chamberRepair[base + k] as number;
      if (r <= bestRepair) {
        // Strictly-less updates the min; equal keeps the lower slot (best stays).
        if (best < 0 || r < bestRepair) {
          bestRepair = r;
          best = k;
        }
      }
    }
    return best;
  }

  /** True if `cid` has a chamber of `type` (public read for the AI's enqueue gates). */
  hasChamber(cid: ColonyId, type: number): boolean {
    return this.hasChamberType(cid, type);
  }

  /**
   * Centre of the colony's first chamber of `type` written into out[0]=x, out[1]=y;
   * returns true if found. The AI uses this to target a Nurse at the Nursery / a
   * Repair at the degraded chamber without allocating. Walks slots in build order
   * (deterministic), so the LOWEST-slot chamber of that type wins ties.
   */
  chamberCentreOfType(cid: ColonyId, type: number, out: Int32Array): boolean {
    const n = this.chamberCount[cid] as number;
    const base = cid * this.maxPer;
    for (let k = 0; k < n; k++) {
      if ((this.chamberType[base + k] as number) === type) {
        out[0] = this.chamberX[base + k] as number;
        out[1] = this.chamberY[base + k] as number;
        return true;
      }
    }
    return false;
  }

  /** Centre of chamber slot k into out[0]=x, out[1]=y; true if k is a live slot. */
  chamberCentreAt(cid: ColonyId, k: number, out: Int32Array): boolean {
    if (k < 0 || k >= (this.chamberCount[cid] as number)) return false;
    const idx = cid * this.maxPer + k;
    out[0] = this.chamberX[idx] as number;
    out[1] = this.chamberY[idx] as number;
    return true;
  }

  // --- Accessors (hash / CLI / tests) ----------------------------------------

  chamberCountOf(cid: ColonyId): number {
    return this.chamberCount[cid] as number;
  }
  /** Repair-meter (integrity) of chamber slot k. */
  chamberRepairAt(cid: ColonyId, k: number): number {
    return this.chamberRepair[cid * this.maxPer + k] as number;
  }
  chamberTypeAt(cid: ColonyId, k: number): number {
    return this.chamberType[cid * this.maxPer + k] as number;
  }
  chamberXAt(cid: ColonyId, k: number): number {
    return this.chamberX[cid * this.maxPer + k] as number;
  }
  chamberYAt(cid: ColonyId, k: number): number {
    return this.chamberY[cid * this.maxPer + k] as number;
  }
  chamberLevelAt(cid: ColonyId, k: number): number {
    return this.chamberLevel[cid * this.maxPer + k] as number;
  }
  chamberTilesAt(cid: ColonyId, k: number): number {
    return this.chamberTiles[cid * this.maxPer + k] as number;
  }
  /**
   * Count of built Storage chambers `cid` holds — satisfies the OPTIONAL
   * `DiagHiveReader.storageChamberCountAt`, so the bottleneck diagnostics can resolve
   * "Storage vs Food at capacity" exactly (0 ⇒ Food, ≥1 ⇒ Storage) instead of heuristically.
   * Pure read over the chamber rows; never touched by the tick path or the state hash.
   */
  storageChamberCountAt(cid: ColonyId): number {
    const n = this.chamberCount[cid] as number;
    const base = cid * this.maxPer;
    let count = 0;
    for (let k = 0; k < n; k++) {
      if ((this.chamberType[base + k] as number) === CHAMBER.Storage) count++;
    }
    return count;
  }
  /** Colony owning tile (x,y), or NIL if unclaimed / out of bounds. */
  territoryOwnerAt(x: number, y: number): number {
    if (!this.grid.inBounds(x, y)) return NIL;
    return this.territoryOwner[this.grid.index(x, y)] as number;
  }
  territoryCountOf(cid: ColonyId): number {
    return this.territoryCount[cid] as number;
  }
}
